/**
 * lib/registerPdf.ts
 * ------------------------------------------------------------------
 * Attendance REGISTER PDF — week or month grid of a class's daily
 * attendance (ported/adapted for LKC from the sibling-app register).
 *
 * Reads the SAME data the Entry screen writes: reportDailyAttendance
 * docs, whose `entries: Record<studentId, AttendanceStatus>` map drives
 * each cell. One column per school day (Mon–Fri), holidays shaded.
 * Boys/Girls present totals per day sit under each class (student.gender
 * is a required 'M'|'F' in LKC, so the split always resolves).
 *
 * Multi-class is native: buildRegisterPdf() renders ONE class PER PAGE.
 *
 * Uses the shared LKC chrome from pdfChrome.ts (drawLetterhead /
 * stampFooters / PDF palette / pageGeom / CONTENT_TOP) — same pattern as
 * marksGridPdf.ts. Holiday awareness is INJECTED (isHoliday/holidayName
 * passed to schoolDays) so this file never hardcodes a calendar; the
 * caller wires in src/lib/holidays.ts. NOTE: those holidays are
 * hardcoded 2026-only today — extend holidays.ts when the year rolls.
 * ------------------------------------------------------------------
 */
import { jsPDF } from 'jspdf';
import autoTable, { type CellHookData, type RowInput, type Styles } from 'jspdf-autotable';
import { PDF, CONTENT_TOP, pageGeom, drawLetterhead, stampFooters } from '@/lib/pdfChrome';
import type { AttendanceStatus } from '@/types';

/** Register cell glyph per status (covers all 5 LKC statuses). */
const GLYPH: Record<AttendanceStatus, string> = {
  present: '/',
  absent: 'A',
  late: 'L',
  excused: 'E',
  sick: 'S',
};

const SHORT_DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export interface RegisterDay {
  /** ISO YYYY-MM-DD */
  date: string;
  /** Short weekday, e.g. 'Mo'. */
  dow: string;
  /** Day-of-month number. */
  dom: number;
  holiday: boolean;
  holidayName?: string;
}

export interface RegisterStudentRow {
  studentId: string;
  fullName: string;
  gender: 'M' | 'F';
}

export interface RegisterClass {
  classId: string;
  className: string;
  students: RegisterStudentRow[];
  /** studentId -> (ISO date -> status). Missing = not recorded that day. */
  marks: Record<string, Record<string, AttendanceStatus>>;
}

export interface RegisterPdfData {
  view: 'week' | 'month';
  /** Human label for the period, e.g. 'Week of 6–10 Jan 2026' or 'January 2026'. */
  periodLabel: string;
  days: RegisterDay[];
  classes: RegisterClass[];
  logoDataUrl?: string | null;
  generatedBy?: string;
}

type HolidayFns = { isHoliday: (d: string) => boolean; holidayName: (d: string) => string | undefined };

const pad2 = (n: number) => String(n).padStart(2, '0');
const isoOf = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

function makeDay(d: Date, { isHoliday, holidayName }: HolidayFns): RegisterDay {
  const iso = isoOf(d);
  return { date: iso, dow: SHORT_DOW[d.getDay()], dom: d.getDate(), holiday: isHoliday(iso), holidayName: holidayName(iso) };
}

/** Weekdays (Mon–Fri) in [startISO, endISO] inclusive, holiday-tagged. */
export function schoolDays(startISO: string, endISO: string, h: HolidayFns): RegisterDay[] {
  const out: RegisterDay[] = [];
  const d = new Date(startISO + 'T12:00:00');
  const end = new Date(endISO + 'T12:00:00');
  while (d <= end) {
    const wd = d.getDay();
    if (wd >= 1 && wd <= 5) out.push(makeDay(new Date(d), h));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

/** Mon–Fri of the ISO week containing `anchorISO`. */
export function weekDaysFor(anchorISO: string, h: HolidayFns): RegisterDay[] {
  const base = new Date(anchorISO + 'T12:00:00');
  const dow = base.getDay(); // 0 Sun .. 6 Sat
  const monday = new Date(base);
  monday.setDate(base.getDate() + (dow === 0 ? -6 : 1 - dow));
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  return schoolDays(isoOf(monday), isoOf(friday), h);
}

/** All weekdays in month `monthISO` ('YYYY-MM'). */
export function monthDaysFor(monthISO: string, h: HolidayFns): RegisterDay[] {
  const [y, m] = monthISO.split('-').map(Number);
  const first = new Date(y, m - 1, 1, 12);
  const last = new Date(y, m, 0, 12); // day 0 of next month = last day of this
  return schoolDays(isoOf(first), isoOf(last), h);
}

const attended = (s: AttendanceStatus | undefined) => s === 'present' || s === 'late';

type RowMeta = { type: 'band' | 'student' | 'total'; zebra?: boolean };

/** Build one class's head/body/rowMeta for its page. */
function buildClassTable(cls: RegisterClass, days: RegisterDay[]) {
  const head: RowInput = ['#', 'Student', ...days.map((d) => `${d.dow}\n${d.dom}`), 'P', 'A', '%'];
  const body: RowInput[] = [];
  const rowMeta: RowMeta[] = [];

  cls.students.forEach((stu, i) => {
    const marks = cls.marks[stu.studentId] || {};
    let present = 0;
    let absent = 0;
    let recorded = 0;
    const dayCells = days.map((d) => {
      const st = marks[d.date];
      if (st) {
        recorded += 1;
        if (attended(st)) present += 1;
        if (st === 'absent') absent += 1;
      }
      return d.holiday ? '' : st ? GLYPH[st] : '';
    });
    const pct = recorded > 0 ? String(Math.round((present / recorded) * 100)) : '';
    body.push([String(i + 1), stu.fullName, ...dayCells, String(present || ''), String(absent || ''), pct]);
    rowMeta.push({ type: 'student', zebra: i % 2 === 1 });
  });

  // Boys / Girls present-per-day totals.
  const totalRow = (label: string, gender: 'M' | 'F'): RowInput => {
    const roster = cls.students.filter((s) => s.gender === gender);
    const perDay = days.map((d) => {
      if (d.holiday) return '';
      const n = roster.reduce((acc, s) => acc + (attended(cls.marks[s.studentId]?.[d.date]) ? 1 : 0), 0);
      return String(n);
    });
    return [{ content: label, colSpan: 2, styles: { fontStyle: 'bold', halign: 'right' } }, ...perDay, '', '', ''];
  };
  body.push(totalRow('Boys present →', 'M'));
  rowMeta.push({ type: 'total' });
  body.push(totalRow('Girls present →', 'F'));
  rowMeta.push({ type: 'total' });

  return { head, body, rowMeta };
}

/** Build the register PDF (one class per page); caller does `.save(name)` or `.output('blob')`. */
export function buildRegisterPdf(data: RegisterPdfData): jsPDF {
  const geom = pageGeom('l');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const dayCount = data.days.length;
  const DAY_COL_START = 2; // cols: 0 '#', 1 'Student', then days
  const DAY_COL_END = DAY_COL_START + dayCount;

  const classes = data.classes.length ? data.classes : [];
  classes.forEach((cls, ci) => {
    if (ci > 0) pdf.addPage();
    const { head, body, rowMeta } = buildClassTable(cls, data.days);

    autoTable(pdf, {
      head: [head],
      body,
      startY: CONTENT_TOP,
      margin: { top: CONTENT_TOP, left: geom.MX, right: geom.MX, bottom: 14 },
      theme: 'plain',
      styles: { font: 'helvetica', fontSize: 7, textColor: PDF.INK, cellPadding: { top: 1, bottom: 1, left: 1, right: 1 }, lineColor: PDF.RULE, lineWidth: 0.1, halign: 'center', valign: 'middle', overflow: 'hidden' },
      headStyles: { fillColor: PDF.NAVY, textColor: PDF.WHITE, fontStyle: 'bold', fontSize: 6.5, halign: 'center', valign: 'middle' },
      columnStyles: {
        0: { cellWidth: 7 },
        1: { cellWidth: 42, halign: 'left' },
        [DAY_COL_END]: { cellWidth: 9, fontStyle: 'bold' },
        [DAY_COL_END + 1]: { cellWidth: 9, fontStyle: 'bold' },
        [DAY_COL_END + 2]: { cellWidth: 10, fontStyle: 'bold' },
      } as Record<number, Partial<Styles>>,
      didParseCell: (h: CellHookData) => {
        const col = h.column.index;
        const isDayCol = col >= DAY_COL_START && col < DAY_COL_END;
        if (h.section === 'head') {
          if (isDayCol && data.days[col - DAY_COL_START]?.holiday) {
            h.cell.styles.fillColor = PDF.GOLD;
            h.cell.styles.textColor = PDF.NAVY;
          }
          return;
        }
        if (h.section !== 'body') return;
        const meta = rowMeta[h.row.index];
        if (!meta) return;
        if (meta.type === 'total') { h.cell.styles.fillColor = PDF.READONLY_BG; return; }
        if (isDayCol && data.days[col - DAY_COL_START]?.holiday) { h.cell.styles.fillColor = PDF.BAND; return; }
        if (meta.zebra) h.cell.styles.fillColor = PDF.ZEBRA;
      },
      didDrawPage: () => {
        drawLetterhead(pdf, {
          title: 'ATTENDANCE REGISTER',
          metaLine: `${cls.className}  ·  ${data.periodLabel}  ·  ${data.view === 'week' ? 'Weekly' : 'Monthly'}`,
          logoDataUrl: data.logoDataUrl,
          geom,
        });
      },
    });

    // Legend under this class's table.
    const finalY = (pdf as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? CONTENT_TOP;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(...PDF.MUTED);
    pdf.text('Key:  / Present   A Absent   L Late   E Excused   S Sick   ·   shaded column = public holiday', geom.MX, Math.min(finalY + 6, geom.PH - 12));
  });

  stampFooters(pdf, { geom, generatedBy: data.generatedBy });
  return pdf;
}
